import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Flame, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import { z } from "zod";
import { motion, useReducedMotion, AnimatePresence } from "framer-motion";
import { trackFormSubmission } from "@/lib/analytics";
import { submitLead } from "@/lib/leads";

const quoteSchema = z.object({
  name: z.string().trim().min(2, "Name must be at least 2 characters").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Please enter a valid email address").max(255, "Email must be less than 255 characters"),
  phone: z.string().trim().min(10, "Please enter a valid phone number").max(20, "Phone number is too long"),
  city: z.string().trim().min(2, "Please enter your city").max(100, "City name is too long"),
  category: z.string().min(1, "Please select a product category"),
  message: z.string().trim().max(2000, "Message must be less than 2000 characters").optional(),
});

type QuoteFormData = z.infer<typeof quoteSchema>;

const productCategories = [
  "Custom Outdoor Kitchen",
  "Modular BBQ Island",
  "Gas Grill",
  "Charcoal Grill",
  "Built-In Grill",
  "Pellet Smoker",
  "Offset Smoker",
  "Pizza Oven",
  "Outdoor Refrigerator",
  "Ice Maker",
  "Accessories",
  "Full Outdoor Kitchen Design",
  "Other",
];

interface BBQQuoteFormProps {
  variant?: "full" | "compact";
  preselectedCategory?: string;
  className?: string;
}

export const BBQQuoteForm = ({ variant = "full", preselectedCategory, className = "" }: BBQQuoteFormProps) => {
  const shouldReduceMotion = useReducedMotion();
  const [formData, setFormData] = useState<Partial<QuoteFormData>>({
    name: "",
    email: "",
    phone: "",
    city: "",
    category: preselectedCategory || "",
    message: "",
  });
  const [errors, setErrors] = useState<Partial<Record<keyof QuoteFormData, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name as keyof QuoteFormData]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrors({});

    try {
      const validated = quoteSchema.parse(formData);
      
      // Save lead to database with notification
      const result = await submitLead('bbq', {
        email: validated.email,
        phone: validated.phone,
        name: validated.name,
        city: validated.city,
        category: validated.category,
        message: validated.message,
        sourcePage: window.location.pathname,
        leadType: 'quote',
      });

      if (!result.success) {
        toast.error("Something went wrong. Please try again or call us directly.");
        return;
      }
      
      // Track successful form submission
      trackFormSubmission('bbq', 'quote_form');
      
      setIsSubmitted(true);
      toast.success("Quote request submitted! We'll contact you within 24 hours.");
    } catch (error) {
      if (error instanceof z.ZodError) {
        const fieldErrors: Partial<Record<keyof QuoteFormData, string>> = {};
        error.errors.forEach((err) => {
          const field = err.path[0] as keyof QuoteFormData;
          fieldErrors[field] = err.message;
        });
        setErrors(fieldErrors);
        toast.error("Please fix the errors in the form");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <motion.div 
        className={`bg-[#1a1a1a] p-8 text-center ${className}`}
        initial={shouldReduceMotion ? {} : { opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
      >
        <motion.div
          initial={shouldReduceMotion ? {} : { scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.1, type: "spring", stiffness: 200 }}
        >
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
        </motion.div>
        <h3 className="text-2xl font-bold text-[#f5e6d3] mb-2">Quote Request Received!</h3>
        <p className="text-[#f5e6d3]/70 mb-6">
          Thank you for your interest. One of our BBQ experts will contact you within 24 hours 
          with a personalized quote.
        </p>
        <Button 
          onClick={() => setIsSubmitted(false)}
          className="bg-orange-500 hover:bg-orange-600 text-white"
        >
          Submit Another Request
        </Button>
      </motion.div>
    );
  }

  return (
    <motion.form 
      onSubmit={handleSubmit} 
      className={`bg-[#1a1a1a] p-6 md:p-8 ${className}`}
      initial={shouldReduceMotion ? {} : { opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
    >
      {variant === "full" && (
        <div className="mb-6">
          <h3 className="text-2xl font-bold text-[#f5e6d3] flex items-center gap-2">
            <Flame className="w-6 h-6 text-orange-500" />
            Get Your Free Quote
          </h3>
          <p className="text-[#f5e6d3]/60 mt-2">
            Fill out the form below and we'll contact you within 24 hours
          </p>
        </div>
      )}

      <div className="space-y-4">
        {/* Name */}
        <div>
          <label htmlFor="quote-name" className="sr-only">Your Name</label>
          <input
            id="quote-name"
            type="text"
            name="name"
            placeholder="Your Name *"
            value={formData.name}
            onChange={handleChange}
            aria-describedby={errors.name ? "quote-name-error" : undefined}
            className={`w-full px-4 py-4 bg-[#2a2520] border-2 ${
              errors.name ? "border-red-500" : "border-transparent focus:border-orange-500"
            } text-[#f5e6d3] placeholder:text-[#f5e6d3]/40 outline-none transition-all duration-200 focus:shadow-[0_0_0_3px_rgba(249,115,22,0.15)]`}
          />
          <AnimatePresence>
            {errors.name && (
              <motion.p 
                id="quote-name-error"
                className="text-red-500 text-sm mt-1"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {errors.name}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        {/* Email */}
        <div>
          <label htmlFor="quote-email" className="sr-only">Email Address</label>
          <input
            id="quote-email"
            type="email"
            name="email"
            placeholder="Email Address *"
            value={formData.email}
            onChange={handleChange}
            aria-describedby={errors.email ? "quote-email-error" : undefined}
            className={`w-full px-4 py-4 bg-[#2a2520] border-2 ${
              errors.email ? "border-red-500" : "border-transparent focus:border-orange-500"
            } text-[#f5e6d3] placeholder:text-[#f5e6d3]/40 outline-none transition-all duration-200 focus:shadow-[0_0_0_3px_rgba(249,115,22,0.15)]`}
          />
          <AnimatePresence>
            {errors.email && (
              <motion.p 
                id="quote-email-error"
                className="text-red-500 text-sm mt-1"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {errors.email}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        {/* Phone */}
        <div>
          <label htmlFor="quote-phone" className="sr-only">Phone Number</label>
          <input
            id="quote-phone"
            type="tel"
            name="phone"
            placeholder="Phone Number *"
            value={formData.phone}
            onChange={handleChange}
            aria-describedby={errors.phone ? "quote-phone-error" : undefined}
            className={`w-full px-4 py-4 bg-[#2a2520] border-2 ${
              errors.phone ? "border-red-500" : "border-transparent focus:border-orange-500"
            } text-[#f5e6d3] placeholder:text-[#f5e6d3]/40 outline-none transition-all duration-200 focus:shadow-[0_0_0_3px_rgba(249,115,22,0.15)]`}
          />
          <AnimatePresence>
            {errors.phone && (
              <motion.p 
                id="quote-phone-error"
                className="text-red-500 text-sm mt-1"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {errors.phone}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        {/* City */}
        <div>
          <label htmlFor="quote-city" className="sr-only">City</label>
          <input
            id="quote-city"
            type="text"
            name="city"
            placeholder="City (e.g., Dallas, Plano, Frisco) *"
            value={formData.city}
            onChange={handleChange}
            aria-describedby={errors.city ? "quote-city-error" : undefined}
            className={`w-full px-4 py-4 bg-[#2a2520] border-2 ${
              errors.city ? "border-red-500" : "border-transparent focus:border-orange-500"
            } text-[#f5e6d3] placeholder:text-[#f5e6d3]/40 outline-none transition-all duration-200 focus:shadow-[0_0_0_3px_rgba(249,115,22,0.15)]`}
          />
          <AnimatePresence>
            {errors.city && (
              <motion.p 
                id="quote-city-error"
                className="text-red-500 text-sm mt-1"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {errors.city}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        {/* Category */}
        <div>
          <label htmlFor="quote-category" className="sr-only">Product Category</label>
          <select
            id="quote-category"
            name="category"
            value={formData.category}
            onChange={handleChange}
            aria-label="Select Product Category"
            className={`w-full px-4 py-4 bg-[#2a2520] border-2 ${
              errors.category ? "border-red-500" : "border-transparent focus:border-orange-500"
            } text-[#f5e6d3] outline-none appearance-none cursor-pointer transition-all duration-200 focus:shadow-[0_0_0_3px_rgba(249,115,22,0.15)]`}
          >
            <option value="" className="text-[#f5e6d3]/40">Select Product Category *</option>
            {productCategories.map((cat) => (
              <option key={cat} value={cat} className="bg-[#2a2520]">{cat}</option>
            ))}
          </select>
          <AnimatePresence>
            {errors.category && (
              <motion.p 
                className="text-red-500 text-sm mt-1"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
              >
                {errors.category}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        {/* Message */}
        {variant === "full" && (
          <div>
            <textarea
              name="message"
              placeholder="Tell us about your project (optional)"
              value={formData.message}
              onChange={handleChange}
              rows={4}
              className={`w-full px-4 py-4 bg-[#2a2520] border-2 ${
                errors.message ? "border-red-500" : "border-transparent focus:border-orange-500"
              } text-[#f5e6d3] placeholder:text-[#f5e6d3]/40 outline-none resize-none transition-all duration-200 focus:shadow-[0_0_0_3px_rgba(249,115,22,0.15)]`}
            />
            <AnimatePresence>
              {errors.message && (
                <motion.p 
                  className="text-red-500 text-sm mt-1"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.2 }}
                >
                  {errors.message}
                </motion.p>
              )}
            </AnimatePresence>
          </div>
        )}

        {/* Submit */}
        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-4 text-lg uppercase tracking-wide disabled:opacity-50 transition-all duration-200 hover:shadow-lg"
        >
          {isSubmitting ? "Submitting..." : "Get My Free Quote"}
        </Button>

        <p className="text-[#f5e6d3]/40 text-xs text-center">
          By submitting, you agree to receive communications from BBQ Island Liquidators. 
          We respect your privacy and will never share your information.
        </p>
      </div>
    </motion.form>
  );
};
